/** Settings › Integrations as tabs (integrations round three, the founder's orders of 2026-10-13), the pure half: which tabs show, which
 *  one an address selects, and which tab an open dialog belongs to. The screen (`IntegrationsSettings.tsx`) declares the tabs and draws them.
 *  - A kind's tab shows only while the kind is in use (at least one account, site, endpoint, webhook of your own or hand-made key); "Add new"
 *    always shows, last.
 *  - An open state in the address names its tab: an item's dialog its kind's tab (a connected site's key, Disconnect and log the WordPress
 *    tab); an add flow its kind's tab when that tab exists, else "Add new"; and an add flow opened on "Add new" stays there.
 *  - Otherwise `?tab=` picks the tab; a tab that is not shown falls back to the first one. */
export type IntegrationTabId = "github" | "wordpress" | "endpoints" | "webhooks" | "api-keys" | "add";
export type KindTabId = Exclude<IntegrationTabId, "add">;
/** what each kind has in use, counted as the founder's order counts it */
export type KindCounts = { github: number; sites: number; endpoints: number; webhooks: number; keys: number };
const COUNT_OF: Record<KindTabId, keyof KindCounts> = { github: "github", wordpress: "sites", endpoints: "endpoints", webhooks: "webhooks", "api-keys": "keys" };
export const countOf = (counts: KindCounts, tab: KindTabId): number => counts[COUNT_OF[tab]];

/** the declared tabs that show: the kinds in use, and "Add new" */
export function tabsInUse<T extends { id: IntegrationTabId }>(tabs: T[], counts: KindCounts): T[] {
  return tabs.filter((t) => t.id === "add" || countOf(counts, t.id) > 0);
}

/** the facts an open state needs to find its tab: which sites are connected and which webhook addresses a site's Connect made */
export type OpenStateFacts = { connectedSites: ReadonlySet<string>; siteWebhooks: ReadonlySet<string> };
/** the dialog an address opens: its kind's tab (or "add" for the request form) and whether it is an add flow */
export type OpenState = { tab: KindTabId | "add"; adding: boolean };

/** which dialog the address opens, if any, and where it belongs */
export function openStateOf(params: URLSearchParams, facts: OpenStateFacts): OpenState | null {
  const get = (k: string) => params.get(k);
  if (get("request")) return { tab: "add", adding: true };
  if (get("connect") === "github" || get("pick")) return { tab: "github", adding: true };
  if (get("disconnect")) return { tab: "github", adding: false };
  const wp = get("wordpress");
  if (wp) return { tab: "wordpress", adding: wp === "connect" };
  const endpoint = get("endpoint");
  if (endpoint) return { tab: "endpoints", adding: endpoint === "new" };
  const webhook = get("webhook");
  if (webhook) return { tab: "webhooks", adding: webhook === "new" };
  const deliveries = get("deliveries");
  if (deliveries) return { tab: facts.siteWebhooks.has(deliveries) ? "wordpress" : "webhooks", adding: false };
  const apikey = get("apikey");
  if (apikey) return apikey === "new" ? { tab: "api-keys", adding: true } : { tab: facts.connectedSites.has(apikey.replace(/^(details|revoke)-/, "")) ? "wordpress" : "api-keys", adding: false };
  return null;
}

/** the tab an open state belongs to among the shown ones: its kind's, else "Add new" for an add flow; null when it has none */
function tabOfState(state: OpenState, shown: readonly IntegrationTabId[]): IntegrationTabId | null {
  if (shown.includes(state.tab)) return state.tab;
  return state.adding ? "add" : null;
}

/** the selected tab: an open state's (an add flow opened on "Add new" stays there), else the asked `?tab=` when shown, else the first */
export function selectedTab(asked: string | null, state: OpenState | null, shown: readonly IntegrationTabId[]): IntegrationTabId {
  const keepAdd = state?.adding && asked === "add";
  const fromState = state && !keepAdd ? tabOfState(state, shown) : null;
  if (fromState) return fromState;
  const named = shown.find((t) => t === asked);
  return named ?? shown[0] ?? "add";
}

/** the `?tab=` to write so the tab stays once the dialog closes: when the address has no tab yet, or an item's dialog names another one
 *  (an add flow never rewrites a tab already asked for: after an add the screen selects the kind's tab itself, before its data arrives) */
export function tabToRemember(asked: string | null, state: OpenState | null, selected: IntegrationTabId): IntegrationTabId | null {
  if (!state || asked === selected) return null;
  if (asked === null || !state.adding) return selected;
  return null;
}
